import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { useToast } from '../hooks/use-toast';
import { usePlatformAuth } from '../context/PlatformAuthContext';
import {
  ShieldCheck, LogOut, Building2, Users, DollarSign, Sparkles, Pause, Lock, Play, Search,
  Briefcase, TrendingUp, Loader2, Activity, AlertOctagon, RefreshCw, UserCog, LifeBuoy,
  Pencil, Trash2, UserCheck, MoreHorizontal, Undo2, HeartPulse, ShieldOff, Zap, History, BookOpen,
  CheckCircle2,
} from 'lucide-react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, BarChart, Bar, PieChart, Pie, Cell, Legend,
} from 'recharts';

const API = `${window.__BACKEND_URL__}/api`;

const PIE_COLORS = ['#1E88E5', '#2CA01C', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6', '#F97316', '#0EA5E9', '#84CC16', '#A855F7', '#64748B'];

const STATUS_TONE = {
  trial: 'bg-blue-900 text-blue-200 border-blue-700',
  grace: 'bg-yellow-900 text-yellow-200 border-yellow-700',
  active: 'bg-emerald-900 text-emerald-200 border-emerald-700',
  past_due: 'bg-amber-900 text-amber-200 border-amber-700',
  suspended: 'bg-orange-900 text-orange-200 border-orange-700',
  locked_out: 'bg-red-900 text-red-200 border-red-700',
  cancelled: 'bg-slate-800 text-slate-300 border-slate-700',
};

const fmt$ = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—';

export default function PlatformDashboard() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { admin, token, logout } = usePlatformAuth();

  useEffect(() => {
    if (!admin) navigate('/platform-admin/signin');
  }, [admin, navigate]);

  const headers = { Authorization: `Bearer ${token}` };

  const [dashboard, setDashboard] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [signupSeries, setSignupSeries] = useState([]);
  const [mrrSeries, setMrrSeries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [billing, setBilling] = useState(null);
  const [billingBusy, setBillingBusy] = useState(false);
  const [sweepPreview, setSweepPreview] = useState(null);
  const [timelineTarget, setTimelineTarget] = useState(null);
  const [timelineData, setTimelineData] = useState(null);
  const [recovery, setRecovery] = useState(null);
  const [stripeHealth, setStripeHealth] = useState(null);
  const [errorStats, setErrorStats] = useState(null);
  const [conversions, setConversions] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [repairingWebhook, setRepairingWebhook] = useState(false);
  const [webhookLogOpen, setWebhookLogOpen] = useState(false);
  const [webhookLog, setWebhookLog] = useState(null);

  const [actionTarget, setActionTarget] = useState(null);  // { id, name, mode: 'suspend' | 'lockout' | 'restore' }
  const [actionRunning, setActionRunning] = useState(false);

  // Support-tools state for the new Edit / Delete / Super-User flows
  const [editCompanyTarget, setEditCompanyTarget] = useState(null);     // company row
  const [editSuperTarget, setEditSuperTarget] = useState(null);         // company row
  const [deleteTarget, setDeleteTarget] = useState(null);               // company row
  const [companyEditForm, setCompanyEditForm] = useState({});
  const [superEditMode, setSuperEditMode] = useState('edit');           // 'edit' | 'promote'
  const [superForm, setSuperForm] = useState({ new_email: '', new_name: '', new_password: '' });
  const [promotePickId, setPromotePickId] = useState('');
  const [companyUsers, setCompanyUsers] = useState([]);
  const [deleteConfirmName, setDeleteConfirmName] = useState('');
  const [supportSaving, setSupportSaving] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [d, c, s, m, b, rec] = await Promise.all([
        axios.get(`${API}/platform/dashboard`, { headers }),
        axios.get(`${API}/platform/companies`, { headers }),
        axios.get(`${API}/platform/charts/signups?days=30`, { headers }),
        axios.get(`${API}/platform/charts/mrr`, { headers }),
        axios.get(`${API}/platform/billing/settings`, { headers }),
        axios.get(`${API}/platform/billing/recovery-stats`, { headers }),
      ]);
      setDashboard(d.data);
      setCompanies(c.data);
      setSignupSeries(s.data);
      setMrrSeries(m.data);
      setBilling(b.data);
      setRecovery(rec.data);
      // Stripe connection light — non-blocking, slower (live Stripe call)
      axios.get(`${API}/platform/billing/stripe-health`, { headers })
        .then((r) => setStripeHealth(r.data))
        .catch((e) => setStripeHealth({ ok: false, error: e?.response?.data?.detail || 'health check failed' }));
      axios.get(`${API}/platform/charts/weekly-conversions`, { headers })
        .then((r) => setConversions(r.data))
        .catch(() => setConversions(null));
      axios.get(`${API}/platform/charts/conversion-funnel`, { headers })
        .then((r) => setFunnel(r.data))
        .catch(() => setFunnel(null));
      // Error Watchdog (Aug 2026) — crash / failed-save alert banner
      axios.get(`${API}/platform/errors/stats`, { headers })
        .then((r) => setErrorStats(r.data))
        .catch(() => setErrorStats(null));
    } catch (err) {
      toast({ title: 'Failed to load Platform HQ', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (token) fetchAll(); }, [token]);  // eslint-disable-line

  const filtered = useMemo(() => {
    let rows = companies;
    if (statusFilter !== 'all') rows = rows.filter((r) => r.subscription_status === statusFilter);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      rows = rows.filter((r) =>
        (r.company_name || '').toLowerCase().includes(q) ||
        (r.super_user_email || '').toLowerCase().includes(q) ||
        (r.industry || '').toLowerCase().includes(q)
      );
    }
    return rows;
  }, [companies, statusFilter, search]);

  const runAction = async () => {
    if (!actionTarget) return;
    setActionRunning(true);
    try {
      const path = actionTarget.mode === 'suspend' ? 'suspend'
                 : actionTarget.mode === 'lockout' ? 'lockout'
                 : 'restore';
      const url = `${API}/platform/companies/${actionTarget.id}/${path}` +
                  (actionTarget.mode === 'restore' ? '?new_status=active' : '');
      const res = await axios.post(url, {}, { headers });
      toast({
        title: actionTarget.mode === 'restore' ? 'Subscription restored' : 'Action applied',
        description: `${actionTarget.name} → ${res.data?.subscription_status || actionTarget.mode}`,
      });
      setActionTarget(null);
      fetchAll();
    } catch (err) {
      toast({ title: 'Action failed', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setActionRunning(false);
    }
  };

  // ── Trial auto-billing controls (Jun 2026) ──────────────────────────────
  const toggleEnforcement = async () => {
    const next = !billing?.enforcement_enabled;
    const msg = next
      ? 'Turn ON auto-billing enforcement?\n\n⚠ ONLY do this in PRODUCTION. Every hour: expired trials are charged on the card on file; companies without a card get a grace-period email to their super user, then are locked out if still unpaid.\n\nIn a preview/test environment this would email REAL customers.'
      : 'Turn OFF auto-billing enforcement? The hourly sweep will stop (no charges, no emails, no lockouts).';
    if (!window.confirm(msg)) return;
    setBillingBusy(true);
    try {
      const res = await axios.post(`${API}/platform/billing/settings`, { enforcement_enabled: next }, { headers });
      setBilling((b) => ({ ...b, enforcement_enabled: res.data.enforcement_enabled }));
      toast({ title: `Auto-billing enforcement ${res.data.enforcement_enabled ? 'ON' : 'OFF'}` });
    } catch (err) {
      toast({ title: 'Failed to update', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setBillingBusy(false);
    }
  };

  const repairWebhook = async () => {
    if (!window.confirm('Repair the Stripe webhook?\n\nThis re-creates the webhook endpoint in Stripe for this environment and stores a fresh signing secret in the database (fixes "Invalid signature" delivery failures). Stale endpoints with the same URL are removed.')) return;
    setRepairingWebhook(true);
    try {
      const res = await axios.post(`${API}/platform/billing/setup-webhook`, {}, { headers });
      toast({ title: 'Webhook repaired', description: `Endpoint ${res.data.endpoint_id} → ${res.data.url}${res.data.stale_removed ? ` (removed ${res.data.stale_removed} stale)` : ''}` });
      axios.get(`${API}/platform/billing/stripe-health`, { headers })
        .then((r) => setStripeHealth(r.data)).catch(() => {});
    } catch (err) {
      toast({ title: 'Webhook repair failed', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setRepairingWebhook(false);
    }
  };

  const openWebhookLog = async () => {
    setWebhookLogOpen(true);
    setWebhookLog(null);
    try {
      const res = await axios.get(`${API}/platform/billing/webhook-events?limit=50`, { headers });
      setWebhookLog(res.data);
    } catch (err) {
      toast({ title: 'Could not load webhook events', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
      setWebhookLogOpen(false);
    }
  };

  const runDrySweep = async () => {
    setBillingBusy(true);
    try {
      const res = await axios.post(`${API}/platform/billing/run-now`, { dry_run: true }, { headers });
      setSweepPreview(res.data);
    } catch (err) {
      toast({ title: 'Preview failed', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setBillingBusy(false);
    }
  };

  const toggleExempt = async (c) => {
    const next = !c.billing_exempt;
    if (!window.confirm(next
      ? `Exempt "${c.company_name}" from auto-billing? It will never be charged or locked out by the sweep.`
      : `Remove billing exemption for "${c.company_name}"? It becomes subject to trial expiry & auto-billing again.`)) return;
    try {
      await axios.post(`${API}/platform/companies/${c.id}/billing-exempt`, { exempt: next }, { headers });
      toast({ title: next ? 'Company exempted from billing' : 'Billing exemption removed', description: c.company_name });
      fetchAll();
    } catch (err) {
      toast({ title: 'Failed', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    }
  };

  const openTimeline = async (c) => {
    setTimelineTarget(c);
    setTimelineData(null);
    try {
      const res = await axios.get(`${API}/platform/companies/${c.id}/billing-timeline`, { headers });
      setTimelineData(res.data);
    } catch (err) {
      toast({ title: 'Failed to load timeline', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
      setTimelineTarget(null);
    }
  };

  // ── Support tools: open Edit Company, Edit Super User, Delete Company ──
  const openEditCompany = (c) => {
    setEditCompanyTarget(c);
    setCompanyEditForm({
      company_name: c.company_name || '',
      industry: c.industry || '',
      address: c.address || '',
      phone: c.phone || '',
      email: c.email || '',
      website: c.website || '',
      billing_email: c.billing_email || '',
    });
  };

  const submitEditCompany = async () => {
    if (!editCompanyTarget) return;
    setSupportSaving(true);
    try {
      // Strip empty strings — backend treats "" as no-change
      const cleaned = Object.fromEntries(
        Object.entries(companyEditForm).filter(([_, v]) => v != null && String(v).trim() !== '')
      );
      const res = await axios.put(`${API}/platform/companies/${editCompanyTarget.id}/edit`, cleaned, { headers });
      toast({ title: 'Company updated', description: `Fields: ${(res.data?.fields_changed || []).join(', ')}` });
      setEditCompanyTarget(null);
      fetchAll();
    } catch (err) {
      toast({ title: 'Update failed', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setSupportSaving(false);
    }
  };

  const openEditSuper = async (c) => {
    setEditSuperTarget(c);
    setSuperEditMode('edit');
    setSuperForm({
      new_email: c.super_user_email || '',
      new_name: c.super_user_name || '',
      new_password: '',
    });
    setPromotePickId('');
    setCompanyUsers([]);
    try {
      const res = await axios.get(`${API}/platform/companies/${c.id}/users`, { headers });
      setCompanyUsers(res.data?.users || []);
    } catch (err) {
      toast({ title: 'Could not load users', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    }
  };

  const submitEditSuper = async () => {
    if (!editSuperTarget) return;
    setSupportSaving(true);
    try {
      const body = superEditMode === 'promote'
        ? { promote_user_id: promotePickId }
        : {
            new_email: superForm.new_email !== editSuperTarget.super_user_email ? superForm.new_email : undefined,
            new_name:  superForm.new_name  !== editSuperTarget.super_user_name  ? superForm.new_name  : undefined,
            new_password: superForm.new_password || undefined,
          };
      const cleaned = Object.fromEntries(Object.entries(body).filter(([_, v]) => v !== undefined && v !== ''));
      if (Object.keys(cleaned).length === 0) {
        toast({ title: 'Nothing to change', description: 'Edit at least one field or pick a different user to promote.', variant: 'destructive' });
        setSupportSaving(false);
        return;
      }
      const res = await axios.put(`${API}/platform/companies/${editSuperTarget.id}/super-user`, cleaned, { headers });
      toast({
        title: 'Super User updated',
        description: `${res.data?.super_user?.email || ''} is now Super User of ${editSuperTarget.company_name}.`,
      });
      setEditSuperTarget(null);
      fetchAll();
    } catch (err) {
      toast({ title: 'Update failed', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setSupportSaving(false);
    }
  };

  const openDelete = (c) => {
    setDeleteTarget(c);
    setDeleteConfirmName('');
  };

  const submitDelete = async () => {
    if (!deleteTarget) return;
    setSupportSaving(true);
    try {
      await axios.delete(`${API}/platform/companies/${deleteTarget.id}`, {
        headers,
        data: { company_name_confirmation: deleteConfirmName },
      });
      toast({
        title: 'Company soft-deleted',
        description: `${deleteTarget.company_name} can be restored within 30 days.`,
      });
      setDeleteTarget(null);
      setDeleteConfirmName('');
      fetchAll();
    } catch (err) {
      toast({ title: 'Delete failed', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setSupportSaving(false);
    }
  };

  const restoreDeleted = async (c) => {
    setSupportSaving(true);
    try {
      await axios.post(`${API}/platform/companies/${c.id}/restore-deleted`, {}, { headers });
      toast({ title: 'Company restored', description: `${c.company_name} is back to trial status.` });
      fetchAll();
    } catch (err) {
      toast({ title: 'Restore failed', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setSupportSaving(false);
    }
  };

  if (loading || !dashboard) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-slate-400 animate-spin" />
      </div>
    );
  }

  const industryRows = (dashboard.industry_breakdown || []).map((b) => ({
    name: b.industry.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
    value: b.count,
  }));

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="platform-dashboard">
      {/* Header */}
      <header className="border-b border-slate-800 bg-slate-900/50 backdrop-blur sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                 style={{ background: 'linear-gradient(135deg, #1E88E5, #2CA01C)' }}>
              <ShieldCheck className="w-5 h-5 text-white" />
            </div>
            <div className="min-w-0">
              <h1 className="text-lg font-bold truncate">Platform HQ</h1>
              <p className="text-xs text-slate-400 truncate">Scalevisory · {admin?.name} · {admin?.role}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={fetchAll} variant="ghost" size="sm" className="text-slate-300 hover:text-white" data-testid="platform-refresh-btn">
              <RefreshCw className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Refresh</span>
            </Button>
            <Button onClick={() => navigate('/platform-admin/manual')} variant="ghost" size="sm" className="text-slate-300 hover:text-white" data-testid="platform-manual-btn">
              <BookOpen className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Manual</span>
            </Button>
            <Button onClick={() => navigate('/platform-admin/support')} variant="ghost" size="sm" className="text-slate-300 hover:text-white" data-testid="platform-support-btn">
              <LifeBuoy className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Support</span>
            </Button>
            <Button onClick={() => navigate('/platform-admin/errors')} variant="ghost" size="sm" className="text-slate-300 hover:text-white" data-testid="platform-errors-btn">
              <AlertOctagon className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Errors</span>
            </Button>
            <Button onClick={() => navigate('/platform-admin/smoke')} variant="ghost" size="sm" className="text-slate-300 hover:text-white" data-testid="platform-smoke-btn">
              <HeartPulse className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Smoke</span>
            </Button>
            <Button onClick={() => navigate('/platform-admin/profile')} variant="ghost" size="sm" className="text-slate-300 hover:text-white" data-testid="platform-profile-btn">
              <UserCog className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Security</span>
            </Button>
            <Button onClick={() => { logout(); navigate('/platform-admin/signin'); }} variant="ghost" size="sm" className="text-slate-300 hover:text-white" data-testid="platform-logout-btn">
              <LogOut className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Sign out</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {/* Counter tiles */}
        <div className="grid grid-cols-2 lg:grid-cols-7 gap-3" data-testid="platform-counter-tiles">
          <Tile icon={Building2} color="#1E88E5" label="Companies" value={dashboard.total_companies} sub={`+${dashboard.signups_this_week} this week`} />
          <Tile icon={Users} color="#8B5CF6" label="Total Users" value={dashboard.total_users} />
          <Tile icon={Sparkles} color="#0EA5E9" label="On Trial" value={dashboard.trial_count} />
          <Tile icon={TrendingUp} color="#F59E0B" label="Trial Pipeline" value={fmt$(dashboard.pending_trial_mrr || 0)} sub={`${dashboard.pending_trial_companies || 0} expired trials to convert`} />
          <Tile icon={DollarSign} color="#2CA01C" label="Paying" value={dashboard.paying_count} sub={`MRR ${fmt$(dashboard.mrr_estimate)}`} highlight />
          <Tile icon={AlertOctagon} color="#F59E0B" label="Suspended" value={dashboard.suspended_count} />
          <Tile icon={Briefcase} color="#EC4899" label="Jobs · 7d" value={dashboard.jobs_this_week} />
        </div>

        {/* Charts row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card className="bg-slate-900 border-slate-800 lg:col-span-2">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-semibold flex items-center gap-2 text-slate-100"><TrendingUp className="w-4 h-4 text-blue-400" />MRR · last 12 months</h2>
                <span className="text-xs text-slate-400">Estimate, paid subs only</span>
              </div>
              <div className="h-48" data-testid="platform-mrr-chart">
                <ResponsiveContainer>
                  <LineChart data={mrrSeries}>
                    <XAxis dataKey="month" stroke="#64748B" fontSize={11} />
                    <YAxis stroke="#64748B" fontSize={11} tickFormatter={(v) => `$${v}`} />
                    <Tooltip contentStyle={{ background: '#0F172A', border: '1px solid #1E293B', color: '#fff' }} formatter={(v) => fmt$(v)} />
                    <Line type="monotone" dataKey="mrr" stroke="#2CA01C" strokeWidth={2.5} dot={{ r: 3, fill: '#2CA01C' }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-5">
              <h2 className="font-semibold flex items-center gap-2 text-slate-100 mb-3"><Activity className="w-4 h-4 text-violet-400" />Industries</h2>
              <div className="h-48" data-testid="platform-industries-chart">
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={industryRows} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={36} outerRadius={70} paddingAngle={2}>
                      {industryRows.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                    </Pie>
                    <Tooltip contentStyle={{ background: '#0F172A', border: '1px solid #1E293B', color: '#fff' }} />
                    <Legend wrapperStyle={{ fontSize: 11, color: '#94A3B8' }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Signups bar chart + Revenue Watch */}
        <div className="grid gap-4 lg:grid-cols-2">
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-5">
            <h2 className="font-semibold flex items-center gap-2 text-slate-100 mb-3"><Building2 className="w-4 h-4 text-blue-400" />Signups · last 30 days</h2>
            <div className="h-44" data-testid="platform-signups-chart">
              <ResponsiveContainer>
                <BarChart data={signupSeries}>
                  <XAxis dataKey="date" stroke="#64748B" fontSize={11} />
                  <YAxis stroke="#64748B" fontSize={11} allowDecimals={false} />
                  <Tooltip contentStyle={{ background: '#0F172A', border: '1px solid #1E293B', color: '#fff' }} />
                  <Bar dataKey="count" fill="#1E88E5" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-900 border-slate-800" data-testid="revenue-watch-card">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold flex items-center gap-2 text-slate-100"><DollarSign className="w-4 h-4 text-emerald-400" />Conversions · last 8 weeks</h2>
              {conversions?.this_week && (
                <span className="text-xs text-emerald-400" data-testid="revenue-watch-this-week">
                  This week: +{conversions.this_week.new_subscriptions} subs · +{fmt$(conversions.this_week.mrr_added)} MRR
                </span>
              )}
            </div>
            <div className="h-44" data-testid="revenue-watch-chart">
              <ResponsiveContainer>
                <BarChart data={conversions?.weeks || []}>
                  <XAxis dataKey="label" stroke="#64748B" fontSize={11} />
                  <YAxis yAxisId="subs" stroke="#64748B" fontSize={11} allowDecimals={false} />
                  <YAxis yAxisId="mrr" orientation="right" stroke="#64748B" fontSize={11} tickFormatter={(v) => `$${v}`} />
                  <Tooltip contentStyle={{ background: '#0F172A', border: '1px solid #1E293B', color: '#fff' }}
                           formatter={(v, name) => name === 'MRR added' ? fmt$(v) : v} />
                  <Bar yAxisId="subs" dataKey="new_subscriptions" name="New subscriptions" fill="#2CA01C" radius={[4, 4, 0, 0]} />
                  <Bar yAxisId="mrr" dataKey="mrr_added" name="MRR added" fill="#F59E0B" radius={[4, 4, 0, 0]} />
                  <Legend wrapperStyle={{ fontSize: 11, color: '#94A3B8' }} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
        </div>

        {/* Conversion funnel */}
        <Card className="bg-slate-900 border-slate-800" data-testid="conversion-funnel-card">
          <CardContent className="p-5">
            <h2 className="font-semibold flex items-center gap-2 text-slate-100 mb-1">
              <TrendingUp className="w-4 h-4 text-amber-400" />Conversion funnel · last {funnel?.days || 30} days
            </h2>
            <p className="text-xs text-slate-500 mb-4">Where companies drop off between the grace email and paying.</p>
            {funnel ? (
              <div className="space-y-3" data-testid="conversion-funnel-steps">
                {funnel.steps.map((s, i) => {
                  const max = Math.max(...funnel.steps.map((x) => x.count), 1);
                  const colors = ['bg-blue-500', 'bg-amber-500', 'bg-emerald-500'];
                  return (
                    <div key={s.label} className="flex items-center gap-3" data-testid={`funnel-step-${i}`}>
                      <div className="w-36 text-sm text-slate-300 shrink-0">{s.label}</div>
                      <div className="flex-1 h-6 bg-slate-800 rounded overflow-hidden">
                        <div className={`h-full ${colors[i]} rounded transition-all`} style={{ width: `${Math.max((s.count / max) * 100, s.count ? 4 : 0)}%` }} />
                      </div>
                      <div className="w-28 text-right text-sm text-slate-200 shrink-0">
                        {s.count}
                        {i > 0 && <span className="text-xs text-slate-500 ml-1.5">({s.rate_from_prev}%)</span>}
                      </div>
                    </div>
                  );
                })}
                {funnel.paid_without_grace > 0 && (
                  <p className="text-xs text-slate-500">+{funnel.paid_without_grace} paid without ever entering grace (direct conversions).</p>
                )}
              </div>
            ) : (
              <p className="text-sm text-slate-500">No funnel data yet.</p>
            )}
          </CardContent>
        </Card>

        {/* Webhook staleness alert (Aug 2026) */}
        {stripeHealth?.ok && stripeHealth.webhook_stale && (
          <div data-testid="webhook-stale-alert" className="rounded-lg border border-red-800 bg-red-950/60 px-4 py-3 flex items-start gap-3">
            <AlertOctagon className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-red-200">
                {stripeHealth.webhook_last_hours != null
                  ? `No Stripe webhook received in ${stripeHealth.webhook_last_hours >= 48 ? `${Math.round(stripeHealth.webhook_last_hours / 24)} days` : `${Math.round(stripeHealth.webhook_last_hours)} hours`}`
                  : 'No Stripe webhook has ever been received in this environment'}
              </p>
              <p className="text-xs text-red-300/80 mt-0.5">
                Payments and subscription changes may not be reaching the server. Check Stripe Dashboard → Developers → Webhooks for failed deliveries, or click "Repair webhook" below to rotate the endpoint.
              </p>
            </div>
          </div>
        )}

        {/* Verified-but-quiet (Aug 2026): endpoint confirmed live in Stripe, just
            no billing events yet — calm info instead of a false alarm. */}
        {stripeHealth?.ok && stripeHealth.webhook_quiet && (
          <div data-testid="webhook-quiet-info" className="rounded-lg border border-emerald-800 bg-emerald-950/50 px-4 py-3 flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-emerald-200">
                Webhook endpoint verified in Stripe — awaiting the next billing event
              </p>
              <p className="text-xs text-emerald-300/80 mt-0.5">
                {stripeHealth.webhook_endpoint_url ? `${stripeHealth.webhook_endpoint_url} is live and enabled. ` : ''}
                No events have arrived since the endpoint was rotated — normal during quiet billing periods. The next subscription change or invoice will confirm delivery automatically.
              </p>
            </div>
          </div>
        )}

        {/* Error Watchdog (Aug 2026): users hit crashes / failed saves in the last 24h */}
        {errorStats != null && (errorStats.errors_24h > 0 || errorStats.crashes_24h > 0 || errorStats.failed_saves_24h > 0) && (
          <div data-testid="error-watchdog-alert" className="rounded-lg border border-amber-700 bg-amber-950/60 px-4 py-3 flex items-start gap-3">
            <AlertOctagon className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-amber-200">
                {errorStats.errors_24h} error report{errorStats.errors_24h === 1 ? '' : 's'} in the last 24 hours
                {(errorStats.crashes_24h > 0 || errorStats.failed_saves_24h > 0) &&
                  ` — including ${[
                    errorStats.crashes_24h > 0 ? `${errorStats.crashes_24h} app crash${errorStats.crashes_24h === 1 ? '' : 'es'}` : null,
                    errorStats.failed_saves_24h > 0 ? `${errorStats.failed_saves_24h} failed save${errorStats.failed_saves_24h === 1 ? '' : 's'}` : null,
                  ].filter(Boolean).join(' and ')}`}
              </p>
              <p className="text-xs text-amber-300/80 mt-0.5">
                Users are hitting errors in production. Open Error Watch to see which pages and tenants are affected.
              </p>
            </div>
            <Button
              size="sm"
              onClick={() => navigate('/platform-admin/errors')}
              className="bg-amber-600 hover:bg-amber-700 text-white shrink-0"
              data-testid="error-watchdog-open-btn"
            >
              Open Error Watch
            </Button>
          </div>
        )}

        {/* Trial auto-billing enforcement */}
        <Card className="bg-slate-900 border-slate-800" data-testid="billing-enforcement-card">
          <CardContent className="p-5">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <h2 className="font-semibold text-slate-100 flex items-center gap-2">
                  <Zap className="w-4 h-4 text-amber-400" />Trial auto-billing enforcement
                  <Badge className={`border ${billing?.enforcement_enabled ? 'bg-emerald-900 text-emerald-200 border-emerald-700' : 'bg-slate-800 text-slate-300 border-slate-700'}`} data-testid="billing-enforcement-status">
                    {billing?.enforcement_enabled ? 'ON' : 'OFF'}
                  </Badge>
                  <Badge
                    className={`border ${stripeHealth == null
                      ? 'bg-slate-800 text-slate-400 border-slate-700'
                      : stripeHealth.ok
                        ? 'bg-emerald-900 text-emerald-200 border-emerald-700'
                        : 'bg-red-900 text-red-200 border-red-700'}`}
                    title={stripeHealth?.error || (stripeHealth?.ok ? `Live Stripe API reachable (${stripeHealth.key_prefix || ''})` : 'Checking…')}
                    data-testid="stripe-connection-light"
                  >
                    <span className={`inline-block w-2 h-2 rounded-full mr-1.5 ${stripeHealth == null ? 'bg-slate-500' : stripeHealth.ok ? 'bg-emerald-400' : 'bg-red-500 animate-pulse'}`} />
                    Stripe {stripeHealth == null ? '…' : stripeHealth.ok ? 'connected' : 'DOWN'}
                  </Badge>
                  <Button
                    size="sm" variant="ghost"
                    className="h-6 px-2 text-xs text-slate-400 hover:text-white"
                    disabled={repairingWebhook}
                    onClick={repairWebhook}
                    title="Re-create the Stripe webhook endpoint and store a fresh signing secret (fixes failed deliveries)"
                    data-testid="repair-webhook-btn"
                  >
                    {repairingWebhook ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Repair webhook'}
                  </Button>
                  <Button
                    size="sm" variant="ghost"
                    className="h-6 px-2 text-xs text-slate-400 hover:text-white"
                    onClick={openWebhookLog}
                    title="Recent Stripe webhook events received by this environment, with processing status"
                    data-testid="webhook-log-btn"
                  >
                    Webhook log
                  </Button>
                </h2>
                <p className="text-xs text-slate-400 mt-1 max-w-xl">
                  When ON, an hourly sweep charges expired trials on the card on file (${billing?.seat_price ?? 5.99}/seat/mo).
                  No card or declined → super user is emailed and a grace period starts (7 days; 14 for legacy expired trials), then the company is locked out if still unpaid.
                </p>
                {stripeHealth && !stripeHealth.ok && (
                  <p className="text-xs text-red-400 mt-1.5 font-mono" data-testid="stripe-health-error">
                    Stripe error: {stripeHealth.error}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Button variant="outline" size="sm" disabled={billingBusy} onClick={runDrySweep} className="border-slate-700 text-slate-200 hover:bg-slate-800" data-testid="billing-preview-sweep-btn">
                  {billingBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Preview sweep'}
                </Button>
                {admin?.role === 'owner' && (
                  <Button size="sm" disabled={billingBusy} onClick={toggleEnforcement}
                    className={billing?.enforcement_enabled ? 'bg-red-600 hover:bg-red-700' : 'bg-emerald-600 hover:bg-emerald-700'}
                    data-testid="billing-enforcement-toggle-btn">
                    {billing?.enforcement_enabled ? 'Turn off' : 'Turn on'}
                  </Button>
                )}
              </div>
            </div>
            {sweepPreview && (
              <div className="mt-4 border border-slate-800 rounded-lg p-3 text-sm" data-testid="billing-sweep-preview">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-slate-300 font-medium">Dry-run preview — what the next sweep would do:</span>
                  <button className="text-xs text-slate-500 hover:text-slate-300" onClick={() => setSweepPreview(null)}>dismiss</button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-5 gap-2 text-xs">
                  <div className="bg-emerald-950/40 border border-emerald-900 rounded p-2 text-emerald-300">
                    Would charge card on file: <strong>{sweepPreview.charged?.length || 0}</strong>
                  </div>
                  <div className="bg-amber-950/40 border border-amber-900 rounded p-2 text-amber-300">
                    Would start grace period: <strong>{sweepPreview.grace_started?.length || 0}</strong>
                  </div>
                  <div className="bg-orange-950/40 border border-orange-900 rounded p-2 text-orange-300">
                    Would send grace reminders: <strong>{sweepPreview.reminders?.length || 0}</strong>
                  </div>
                  <div className="bg-red-950/40 border border-red-900 rounded p-2 text-red-300">
                    Would lock out (grace over): <strong>{sweepPreview.locked_out?.length || 0}</strong>
                  </div>
                  <div className="bg-purple-950/40 border border-purple-900 rounded p-2 text-purple-300">
                    Would send win-back emails: <strong>{sweepPreview.winbacks?.length || 0}</strong>
                  </div>
                </div>
              </div>
            )}
            {recovery && (
              <div className="mt-4 border border-slate-800 rounded-lg p-3" data-testid="billing-recovery-report">
                <p className="text-xs font-semibold text-slate-300 mb-2">Recovery report — dunning funnel to date</p>
                <div className="grid grid-cols-2 sm:grid-cols-6 gap-2 text-xs">
                  <div className="bg-slate-800/60 rounded p-2">
                    <div className="text-slate-400">Grace started</div>
                    <div className="text-base font-semibold text-amber-300" data-testid="recovery-grace-count">{recovery.grace_started}</div>
                  </div>
                  <div className="bg-slate-800/60 rounded p-2">
                    <div className="text-slate-400">Reminders sent</div>
                    <div className="text-base font-semibold text-orange-300">{recovery.reminders_sent}</div>
                  </div>
                  <div className="bg-slate-800/60 rounded p-2">
                    <div className="text-slate-400">Locked out</div>
                    <div className="text-base font-semibold text-red-300">{recovery.locked_out}</div>
                  </div>
                  <div className="bg-slate-800/60 rounded p-2">
                    <div className="text-slate-400">Win-backs sent</div>
                    <div className="text-base font-semibold text-purple-300">{recovery.winbacks_sent}</div>
                  </div>
                  <div className="bg-slate-800/60 rounded p-2">
                    <div className="text-slate-400">Recovered (paying)</div>
                    <div className="text-base font-semibold text-emerald-300" data-testid="recovery-recovered-count">
                      {recovery.recovered_total}
                      <span className="text-[10px] text-slate-400 font-normal ml-1">{recovery.grace_conversion_rate}% of grace</span>
                    </div>
                  </div>
                  <div className="bg-slate-800/60 rounded p-2">
                    <div className="text-slate-400">Won back</div>
                    <div className="text-base font-semibold text-emerald-300">
                      {recovery.recovered_after_winback}
                      <span className="text-[10px] text-slate-400 font-normal ml-1">{recovery.winback_conversion_rate}% of win-backs</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Companies table */}
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-5">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
              <h2 className="font-semibold text-slate-100 flex items-center gap-2"><Building2 className="w-4 h-4" />All companies <span className="text-xs text-slate-400">({filtered.length} of {companies.length})</span></h2>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
                  <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search…" className="pl-8 bg-slate-800 border-slate-700 text-white w-44 sm:w-60" data-testid="platform-search" />
                </div>
                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="bg-slate-800 border border-slate-700 rounded-md px-2 py-1 text-sm text-white" data-testid="platform-status-filter">
                  <option value="all">All</option>
                  <option value="trial">Trial</option>
                  <option value="grace">Grace</option>
                  <option value="active">Active</option>
                  <option value="past_due">Past due</option>
                  <option value="suspended">Suspended</option>
                  <option value="locked_out">Locked out</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
            </div>

            <div className="overflow-x-auto" data-testid="platform-companies-table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-slate-400 border-b border-slate-800">
                    <th className="py-2 pr-3">Company</th>
                    <th className="py-2 pr-3">Industry</th>
                    <th className="py-2 pr-3">Status</th>
                    <th className="py-2 pr-3 text-right">Seats</th>
                    <th className="py-2 pr-3 text-right">MRR</th>
                    <th className="py-2 pr-3">Signed up</th>
                    <th className="py-2 pr-3">Last active</th>
                    <th className="py-2 pr-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((c) => (
                    <tr key={c.id} className="border-b border-slate-800 hover:bg-slate-800/40" data-testid={`platform-row-${c.id}`}>
                      <td className="py-3 pr-3">
                        <div className="font-medium text-slate-100">{c.company_name}</div>
                        <div className="text-xs text-slate-400">{c.super_user_email || '—'}</div>
                      </td>
                      <td className="py-3 pr-3 text-slate-300 capitalize">{(c.industry || '').replace(/_/g, ' ')}</td>
                      <td className="py-3 pr-3">
                        <Badge className={`border ${STATUS_TONE[c.subscription_status] || STATUS_TONE.trial}`}>{c.subscription_status}</Badge>
                        {c.subscription_status === 'trial' && c.trial_days_left != null && (
                          <div className={`text-[11px] mt-1 ${c.trial_days_left < 0 ? 'text-red-400' : 'text-blue-300'}`} data-testid={`trial-days-${c.id}`}>
                            {c.trial_days_left >= 0 ? `${c.trial_days_left}d left in trial` : `expired ${Math.abs(c.trial_days_left)}d ago`}
                          </div>
                        )}
                        {(c.subscription_status === 'grace' || c.subscription_status === 'past_due') && c.grace_period_until && (
                          <div className="text-[11px] mt-1 text-amber-300">grace ends {fmtDate(c.grace_period_until)}</div>
                        )}
                        {c.billing_exempt && (
                          <div className="text-[11px] mt-1 text-purple-300" data-testid={`billing-exempt-tag-${c.id}`}>billing exempt</div>
                        )}
                      </td>
                      <td className="py-3 pr-3 text-right text-slate-200">{c.seats}</td>
                      <td className="py-3 pr-3 text-right">
                        <span className={c.mrr_contribution > 0 ? 'text-emerald-400' : 'text-slate-500'}>{fmt$(c.mrr_contribution)}</span>
                      </td>
                      <td className="py-3 pr-3 text-slate-400 text-xs whitespace-nowrap">{fmtDate(c.created_at)}</td>
                      <td className="py-3 pr-3 text-slate-400 text-xs whitespace-nowrap">{fmtDate(c.last_activity)}</td>
                      <td className="py-3 pr-3">
                        <div className="flex items-center justify-end gap-1 flex-wrap">
                          {/* Edit company + Edit super user — always available */}
                          <Button size="sm" variant="ghost" onClick={() => openEditCompany(c)} className="text-slate-300 hover:text-white hover:bg-slate-800 h-7 px-2" data-testid={`platform-edit-${c.id}`} title="Edit company profile">
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => openEditSuper(c)} className="text-blue-300 hover:text-blue-200 hover:bg-blue-950/40 h-7 px-2" data-testid={`platform-edit-super-${c.id}`} title="Edit Super User">
                            <UserCheck className="w-3.5 h-3.5" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => openTimeline(c)} className="text-slate-400 hover:text-cyan-300 hover:bg-slate-800 h-7 px-2" data-testid={`platform-timeline-${c.id}`} title="Billing timeline">
                            <History className="w-3.5 h-3.5" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => toggleExempt(c)}
                            className={`h-7 px-2 ${c.billing_exempt ? 'text-purple-300 hover:text-purple-200 hover:bg-purple-950/40' : 'text-slate-400 hover:text-purple-300 hover:bg-slate-800'}`}
                            data-testid={`platform-exempt-${c.id}`}
                            title={c.billing_exempt ? 'Billing exempt — click to re-include in auto-billing' : 'Exempt from auto-billing'}>
                            <ShieldOff className="w-3.5 h-3.5" />
                          </Button>

                          {/* Subscription state actions */}
                          {c.deleted_at ? (
                            <Button size="sm" variant="ghost" onClick={() => restoreDeleted(c)} className="text-emerald-400 hover:text-emerald-300 hover:bg-emerald-950/40 h-7 px-2" data-testid={`platform-restore-deleted-${c.id}`} title="Restore deleted company">
                              <Undo2 className="w-3.5 h-3.5 mr-1" />Restore
                            </Button>
                          ) : c.subscription_status === 'suspended' || c.subscription_status === 'locked_out' ? (
                            <Button size="sm" variant="ghost" onClick={() => setActionTarget({ id: c.id, name: c.company_name, mode: 'restore' })} className="text-emerald-400 hover:text-emerald-300 hover:bg-emerald-950/40 h-7 px-2" data-testid={`platform-restore-${c.id}`}>
                              <Play className="w-3.5 h-3.5 mr-1" />Restore
                            </Button>
                          ) : (
                            <>
                              <Button size="sm" variant="ghost" onClick={() => setActionTarget({ id: c.id, name: c.company_name, mode: 'suspend' })} className="text-orange-400 hover:text-orange-300 hover:bg-orange-950/40 h-7 px-2" data-testid={`platform-suspend-${c.id}`} title="Suspend (read-only)">
                                <Pause className="w-3.5 h-3.5" />
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => setActionTarget({ id: c.id, name: c.company_name, mode: 'lockout' })} className="text-red-400 hover:text-red-300 hover:bg-red-950/40 h-7 px-2" data-testid={`platform-lockout-${c.id}`} title="Lock out users">
                                <Lock className="w-3.5 h-3.5" />
                              </Button>
                            </>
                          )}

                          {/* Soft delete — only show if not already deleted */}
                          {!c.deleted_at && (
                            <Button size="sm" variant="ghost" onClick={() => openDelete(c)} className="text-red-400 hover:text-red-300 hover:bg-red-950/40 h-7 px-2" data-testid={`platform-delete-${c.id}`} title="Delete company (reversible 30 days)">
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {filtered.length === 0 && (
                    <tr><td colSpan={8} className="py-8 text-center text-slate-500">No companies match this filter.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Action confirm dialog */}
      {/* Billing timeline dialog (Jun 2026 dunning history) */}
      {/* Webhook event log (Aug 2026) */}
      <Dialog open={webhookLogOpen} onOpenChange={(o) => { if (!o) setWebhookLogOpen(false); }}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-w-2xl" data-testid="webhook-log-dialog">
          <DialogHeader>
            <DialogTitle>Stripe webhook events</DialogTitle>
            <DialogDescription className="text-slate-400">
              Most recent events received by this environment{webhookLog ? ` (${webhookLog.total} total)` : ''}.
            </DialogDescription>
          </DialogHeader>
          {webhookLog == null ? (
            <div className="py-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
          ) : webhookLog.items.length === 0 ? (
            <p className="text-sm text-slate-500 py-4" data-testid="webhook-log-empty">No webhook events received yet in this environment.</p>
          ) : (
            <div className="max-h-96 overflow-y-auto -mx-1 px-1">
              <table className="w-full text-xs">
                <thead className="text-slate-500 text-left sticky top-0 bg-slate-900">
                  <tr><th className="py-1.5 pr-2">Received</th><th className="py-1.5 pr-2">Event</th><th className="py-1.5 pr-2">Status</th><th className="py-1.5">Mode</th></tr>
                </thead>
                <tbody data-testid="webhook-log-rows">
                  {webhookLog.items.map((ev) => (
                    <tr key={ev.id} className="border-t border-slate-800">
                      <td className="py-1.5 pr-2 text-slate-400 whitespace-nowrap">{ev.received_at ? new Date(ev.received_at).toLocaleString() : '—'}</td>
                      <td className="py-1.5 pr-2 text-slate-200 font-mono">{ev.type}</td>
                      <td className="py-1.5 pr-2">
                        <Badge className={`border text-[10px] ${
                          ev.status === 'processed' ? 'bg-emerald-900 text-emerald-200 border-emerald-700'
                          : ev.status === 'error' ? 'bg-red-900 text-red-200 border-red-700'
                          : 'bg-slate-800 text-slate-300 border-slate-700'}`}
                          title={ev.error || ''}>
                          {ev.status || '—'}
                        </Badge>
                      </td>
                      <td className="py-1.5 text-slate-400">{ev.livemode ? 'live' : 'test'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!timelineTarget} onOpenChange={(o) => { if (!o) { setTimelineTarget(null); setTimelineData(null); } }}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-w-lg" data-testid="billing-timeline-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-4 h-4 text-cyan-400" />
              Billing timeline — {timelineTarget?.company_name}
            </DialogTitle>
          </DialogHeader>
          {!timelineData ? (
            <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-500" /></div>
          ) : timelineData.events.length === 0 ? (
            <p className="text-sm text-slate-400 py-4" data-testid="billing-timeline-empty">No billing events yet.</p>
          ) : (
            <div className="max-h-96 overflow-y-auto pr-1 space-y-0" data-testid="billing-timeline-events">
              {timelineData.events.map((e, i) => {
                const meta = {
                  signed_up: { label: 'Signed up', dot: 'bg-blue-400' },
                  trial_end: { label: 'Trial ended', dot: 'bg-sky-400' },
                  grace_started: { label: 'Grace period started', dot: 'bg-amber-400' },
                  reminder_sent: { label: 'Grace reminder emailed', dot: 'bg-orange-400' },
                  payment_failed: { label: 'Payment failed', dot: 'bg-red-400' },
                  charged: { label: 'Card charged — subscription started', dot: 'bg-emerald-400' },
                  subscription_activated: { label: 'Subscription activated', dot: 'bg-emerald-400' },
                  locked_out: { label: 'Account locked out', dot: 'bg-red-500' },
                  winback_sent: { label: 'Win-back email sent', dot: 'bg-purple-400' },
                  restored: { label: 'Restored by Platform HQ', dot: 'bg-cyan-400' },
                }[e.type] || { label: e.type, dot: 'bg-slate-400' };
                const detailBits = [];
                if (e.details?.reason) detailBits.push(e.details.reason);
                if (e.details?.amount) detailBits.push(`$${Number(e.details.amount).toFixed(2)}/mo`);
                if (e.details?.days_left != null) detailBits.push(`${e.details.days_left}d left`);
                if (e.details?.by) detailBits.push(`by ${e.details.by}`);
                return (
                  <div key={i} className="flex gap-3 pb-4 relative">
                    <div className="flex flex-col items-center">
                      <span className={`w-2.5 h-2.5 rounded-full mt-1 shrink-0 ${meta.dot}`} />
                      {i < timelineData.events.length - 1 && <span className="w-px flex-1 bg-slate-700 mt-1" />}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm text-slate-200">
                        {meta.label}
                        {e.synthesized && <span className="ml-1.5 text-[10px] text-slate-500">(from records)</span>}
                      </p>
                      {detailBits.length > 0 && <p className="text-xs text-slate-400 truncate">{detailBits.join(' · ')}</p>}
                      <p className="text-[11px] text-slate-500">{new Date(e.at).toLocaleString()}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Suspend/Lockout/Restore action dialog */}
      <Dialog open={!!actionTarget} onOpenChange={(o) => { if (!o) setActionTarget(null); }}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100" data-testid="platform-action-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {actionTarget?.mode === 'suspend' && (<><Pause className="w-5 h-5 text-orange-400" />Soft-suspend subscription</>)}
              {actionTarget?.mode === 'lockout' && (<><Lock className="w-5 h-5 text-red-400" />Lock out company</>)}
              {actionTarget?.mode === 'restore' && (<><Play className="w-5 h-5 text-emerald-400" />Restore subscription</>)}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {actionTarget?.mode === 'suspend' && (<><strong className="text-slate-200">{actionTarget?.name}</strong> users will still be able to sign in but the app becomes read-only with a payment-required banner. Use for non-payment / billing issues.</>)}
              {actionTarget?.mode === 'lockout' && (<><strong className="text-slate-200">{actionTarget?.name}</strong> users will be blocked at the sign-in screen. Reserve for security incidents or contract terminations.</>)}
              {actionTarget?.mode === 'restore' && (<>Restore <strong className="text-slate-200">{actionTarget?.name}</strong> to active status. Users will be able to use the app normally again.</>)}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setActionTarget(null)} disabled={actionRunning} data-testid="platform-action-cancel">Cancel</Button>
            <Button onClick={runAction} disabled={actionRunning}
              className={actionTarget?.mode === 'restore' ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                       : actionTarget?.mode === 'lockout' ? 'bg-red-600 hover:bg-red-700 text-white'
                       : 'bg-orange-600 hover:bg-orange-700 text-white'}
              data-testid="platform-action-confirm"
            >
              {actionRunning
                ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Working…</>)
                : actionTarget?.mode === 'restore' ? 'Restore now'
                : actionTarget?.mode === 'lockout' ? 'Lock out'
                : 'Suspend'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Company dialog */}
      <Dialog open={!!editCompanyTarget} onOpenChange={(o) => { if (!o) setEditCompanyTarget(null); }}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-w-lg" data-testid="platform-edit-company-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Pencil className="w-5 h-5 text-blue-400" />Edit company profile</DialogTitle>
            <DialogDescription className="text-slate-400">
              Fix typos in name, industry, or contact info for <strong className="text-slate-200">{editCompanyTarget?.company_name}</strong>.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[60vh] overflow-y-auto pr-1">
            <div className="sm:col-span-2">
              <Label className="text-slate-300 text-xs">Company name</Label>
              <Input value={companyEditForm.company_name || ''} onChange={(e) => setCompanyEditForm({ ...companyEditForm, company_name: e.target.value })} className="bg-slate-800 border-slate-700 text-white" data-testid="edit-company-name" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-slate-300 text-xs">Industry</Label>
              <Select value={companyEditForm.industry || ''} onValueChange={(v) => setCompanyEditForm({ ...companyEditForm, industry: v })}>
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="edit-company-industry"><SelectValue placeholder="Select industry" /></SelectTrigger>
                <SelectContent>
                  {[
                    ['cleaning','Cleaning'],['landscaping','Landscaping'],['hvac','HVAC'],['construction','Construction'],
                    ['facilities_mgmt','Facilities Management'],['property_mgmt','Property Management'],['moving','Moving'],
                    ['security','Security'],['pest_control','Pest Control'],['plumbing','Plumbing'],
                    ['electrical','Electrical'],['other','Other'],
                  ].map(([v,l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2">
              <Label className="text-slate-300 text-xs">Address</Label>
              <Input value={companyEditForm.address || ''} onChange={(e) => setCompanyEditForm({ ...companyEditForm, address: e.target.value })} className="bg-slate-800 border-slate-700 text-white" data-testid="edit-company-address" />
            </div>
            <div>
              <Label className="text-slate-300 text-xs">Phone</Label>
              <Input value={companyEditForm.phone || ''} onChange={(e) => setCompanyEditForm({ ...companyEditForm, phone: e.target.value })} className="bg-slate-800 border-slate-700 text-white" data-testid="edit-company-phone" />
            </div>
            <div>
              <Label className="text-slate-300 text-xs">Website</Label>
              <Input value={companyEditForm.website || ''} onChange={(e) => setCompanyEditForm({ ...companyEditForm, website: e.target.value })} className="bg-slate-800 border-slate-700 text-white" data-testid="edit-company-website" />
            </div>
            <div>
              <Label className="text-slate-300 text-xs">Public email</Label>
              <Input type="email" value={companyEditForm.email || ''} onChange={(e) => setCompanyEditForm({ ...companyEditForm, email: e.target.value })} className="bg-slate-800 border-slate-700 text-white" data-testid="edit-company-email" />
            </div>
            <div>
              <Label className="text-slate-300 text-xs">Billing email</Label>
              <Input type="email" value={companyEditForm.billing_email || ''} onChange={(e) => setCompanyEditForm({ ...companyEditForm, billing_email: e.target.value })} className="bg-slate-800 border-slate-700 text-white" data-testid="edit-company-billing-email" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditCompanyTarget(null)} disabled={supportSaving}>Cancel</Button>
            <Button onClick={submitEditCompany} disabled={supportSaving}
                    className="text-white font-semibold"
                    style={{ background: 'linear-gradient(135deg, #1E88E5, #2CA01C)' }}
                    data-testid="edit-company-save">
              {supportSaving ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</>) : 'Save changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Super User dialog */}
      <Dialog open={!!editSuperTarget} onOpenChange={(o) => { if (!o) setEditSuperTarget(null); }}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-w-lg" data-testid="platform-edit-super-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><UserCheck className="w-5 h-5 text-blue-400" />Edit Super User</DialogTitle>
            <DialogDescription className="text-slate-400">
              Manage the Super User of <strong className="text-slate-200">{editSuperTarget?.company_name}</strong>.
              Current: <code className="text-slate-300">{editSuperTarget?.super_user_email || 'none'}</code>
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 mb-3">
            <Button variant={superEditMode === 'edit' ? 'default' : 'ghost'} size="sm"
                    onClick={() => setSuperEditMode('edit')}
                    className={superEditMode === 'edit' ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'text-slate-300'}
                    data-testid="super-mode-edit">
              Edit current user
            </Button>
            <Button variant={superEditMode === 'promote' ? 'default' : 'ghost'} size="sm"
                    onClick={() => setSuperEditMode('promote')}
                    className={superEditMode === 'promote' ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'text-slate-300'}
                    data-testid="super-mode-promote">
              Promote a different user
            </Button>
          </div>

          {superEditMode === 'edit' ? (
            <div className="space-y-3">
              <div>
                <Label className="text-slate-300 text-xs">Email</Label>
                <Input type="email" value={superForm.new_email} onChange={(e) => setSuperForm({ ...superForm, new_email: e.target.value })} className="bg-slate-800 border-slate-700 text-white" data-testid="super-edit-email" />
                <p className="text-[11px] text-slate-500 mt-1">Tip: lowercased automatically on save.</p>
              </div>
              <div>
                <Label className="text-slate-300 text-xs">Name</Label>
                <Input value={superForm.new_name} onChange={(e) => setSuperForm({ ...superForm, new_name: e.target.value })} className="bg-slate-800 border-slate-700 text-white" data-testid="super-edit-name" />
              </div>
              <div>
                <Label className="text-slate-300 text-xs">New password (leave blank to keep)</Label>
                <Input type="password" placeholder="At least 12 chars, mixed case, digit & symbol" value={superForm.new_password} onChange={(e) => setSuperForm({ ...superForm, new_password: e.target.value })} className="bg-slate-800 border-slate-700 text-white" data-testid="super-edit-password" />
                <p className="text-[11px] text-slate-500 mt-1">If set, the user is forced to choose a new one on next sign-in.</p>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <Label className="text-slate-300 text-xs">Pick a user to promote to Super User</Label>
              <Select value={promotePickId} onValueChange={setPromotePickId}>
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="super-promote-picker"><SelectValue placeholder={`Select from ${companyUsers.length} users`} /></SelectTrigger>
                <SelectContent>
                  {companyUsers
                    .filter(u => !u.is_super_user) // current super is excluded
                    .map(u => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.email} {u.name ? `· ${u.name}` : ''} {u.role ? `(${u.role})` : ''}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-amber-300 bg-amber-950/30 border border-amber-900/50 rounded p-2">
                The current Super User will be demoted to a regular admin. They keep all other access.
              </p>
            </div>
          )}

          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditSuperTarget(null)} disabled={supportSaving}>Cancel</Button>
            <Button onClick={submitEditSuper} disabled={supportSaving}
                    className="text-white font-semibold"
                    style={{ background: 'linear-gradient(135deg, #1E88E5, #2CA01C)' }}
                    data-testid="super-edit-save">
              {supportSaving ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</>) : (superEditMode === 'promote' ? 'Promote user' : 'Save changes')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Soft-delete company dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) { setDeleteTarget(null); setDeleteConfirmName(''); } }}>
        <DialogContent className="bg-slate-900 border-red-900/60 text-slate-100 max-w-lg" data-testid="platform-delete-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-300"><Trash2 className="w-5 h-5" />Delete company (reversible)</DialogTitle>
            <DialogDescription className="text-slate-400">
              <strong className="text-slate-200">{deleteTarget?.company_name}</strong> will be locked out and hidden from active customer lists. No data is physically erased.
              You can restore the company within <strong>30 days</strong> using the green Restore button on this dashboard.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Label className="text-slate-300 text-xs">
              Type the company name exactly to confirm: <code className="bg-slate-800 px-1.5 py-0.5 rounded text-red-300">{deleteTarget?.company_name}</code>
            </Label>
            <Input
              value={deleteConfirmName}
              onChange={(e) => setDeleteConfirmName(e.target.value)}
              placeholder={deleteTarget?.company_name || ''}
              className="bg-slate-800 border-slate-700 text-white font-mono"
              data-testid="platform-delete-confirm-input"
            />
            <p className="text-xs text-amber-300 bg-amber-950/30 border border-amber-900/50 rounded p-2">
              Effective immediately, all users of this company will be unable to sign in. Stripe subscriptions are NOT auto-cancelled — handle that in your Stripe dashboard if needed.
            </p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setDeleteTarget(null); setDeleteConfirmName(''); }} disabled={supportSaving}>Cancel</Button>
            <Button onClick={submitDelete}
                    disabled={supportSaving || deleteConfirmName.trim() !== (deleteTarget?.company_name || '').trim()}
                    className="bg-red-600 hover:bg-red-700 text-white"
                    data-testid="platform-delete-confirm-btn">
              {supportSaving ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Deleting…</>) : (<><Trash2 className="w-4 h-4 mr-2" />Delete company</>)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Tile helper used by the counter cards above

const Tile = ({ icon: Icon, label, value, sub, color, highlight }) => (
  <div className={`rounded-lg border p-3 ${highlight ? 'border-emerald-700 bg-emerald-950/30' : 'border-slate-800 bg-slate-900'}`}>
    <div className="flex items-center gap-2 mb-1">
      <Icon className="w-4 h-4" style={{ color }} />
      <span className="text-xs uppercase tracking-wider text-slate-400">{label}</span>
    </div>
    <div className="text-2xl font-bold text-slate-100">{value}</div>
    {sub && <div className="text-[11px] text-slate-400 mt-0.5">{sub}</div>}
  </div>
);
